import type { EditorGraph } from '../../domain/editor-graph';
import { getNodeDefinition } from '../../nodes/registry/node-registry';
import type { EdgeTypeId } from '@/domain/funnel/edge-system';

/** SPEC-006 plan §11 — a proposed connection before it becomes an edge. */
export interface ConnectionCandidate {
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
}

export interface ConnectionValidationResult {
  valid: boolean;
  reason?: string;
}

function ok(): ConnectionValidationResult {
  return { valid: true };
}
function reject(reason: string): ConnectionValidationResult {
  return { valid: false, reason };
}

/**
 * SPEC-006 §18 Structural Validation / plan §12. Pure function: no store,
 * no side effects. Cycles are explicitly allowed (§20/§21) — this never
 * runs acyclic/DAG validation.
 */
export function validateConnection(
  graph: EditorGraph,
  candidate: ConnectionCandidate,
  existingEdgeType: EdgeTypeId = 'standard',
): ConnectionValidationResult {
  const source = graph.nodes.find((node) => node.id === candidate.source);
  const target = graph.nodes.find((node) => node.id === candidate.target);

  if (!source) return reject('Source node does not exist.');
  if (!target) return reject('Target node does not exist.');

  // EDGE-REQ-010: self-loop is blocked in V1.
  if (candidate.source === candidate.target) return reject('A step cannot connect to itself.');

  const sourceDefinition = getNodeDefinition(source.type ?? '');
  const targetDefinition = getNodeDefinition(target.type ?? '');

  // EDGE-REQ-037: unknown/fallback nodes preserve existing edges but never
  // accept new connections.
  if (!sourceDefinition) return reject('Source step type does not support new connections.');
  if (!targetDefinition) return reject('Target step type does not support new connections.');

  if (!sourceDefinition.capabilities.canConnectOut) {
    return reject(`${sourceDefinition.label} cannot start a connection.`);
  }
  if (!targetDefinition.capabilities.canConnectIn) {
    return reject(`${targetDefinition.label} cannot receive a connection.`);
  }

  // EDGE-REQ-012: no exact duplicate relation (same source/target/handles/type).
  const isDuplicate = graph.edges.some(
    (edge) =>
      edge.source === candidate.source &&
      edge.target === candidate.target &&
      (edge.sourceHandle ?? null) === (candidate.sourceHandle ?? null) &&
      (edge.targetHandle ?? null) === (candidate.targetHandle ?? null) &&
      (edge.type ?? 'standard') === existingEdgeType,
  );
  if (isDuplicate) return reject('This connection already exists.');

  return ok();
}
